import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { AAROH_IMAGES } from "../../data/images.js";
import { ArrowLink, Btn, Kicker, TEAM_SIZE, clamp01 } from "./shared.jsx";
import "./about.css";

const CHAPTERS = [
  {
    id: "ch-1",
    date: "Dec 2025",
    title: "The spark",
    body: [
      "Moksh Sindhwani and Tanishq Aggarwal, both 16, attended Season 6 of the Young Changemakers Bootcamp on the IIT Delhi campus. Tale of Humankind Foundation organised it.",
      "They met other students with ideas of their own, and came away wanting more young people to have that kind of room to build.",
    ],
    media: { type: "card" },
  },
  {
    id: "ch-2",
    date: "Dec 2025",
    title: "GRID starts",
    body: [
      "The same month, they started Project GRID. The aim was to support early ideas from young people and help them grow into work that serves other people.",
      `They brought in a student team for programmes, outreach and technology. Today ${TEAM_SIZE} school students run GRID, and each of them has a named job.`,
    ],
    media: {
      type: "photo",
      src: "/images/grid-photo-06-p7.jpg",
      alt: "Co-founders Moksh Sindhwani and Tanishq Aggarwal smiling together",
      caption: "Moksh and Tanishq. This photo is from Metamorphosis, May 2026.",
      shape: "wide",
    },
    links: [["Meet the team", "/team"]],
  },
  {
    id: "ch-3",
    date: "Jan 2026",
    title: "Aurora launches",
    body: [
      "Aurora is GRID's own programme: a year-long global hackathon and changemaking festival for students in Grades 8 to 12. It was built so students with limited resources and slow internet could still take part.",
      "Jerovin Floyd Vincent Joseph won overall with ChillGui, a project that makes everyday apps easier for older people to use. The Top 20 teams entered incubation.",
    ],
    media: {
      type: "portrait",
      src: "/images/grid-photo-10-p8.jpg",
      alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his prize laptop",
      caption: "Aurora overall winner Jerovin Floyd with his prize laptop.",
      stats: [["1,500", "participants"], ["27+", "countries"]],
    },
    links: [["See Aurora's results", "/programmes/aurora"]],
  },
  {
    id: "ch-4",
    date: "May 2026",
    title: "Outreach for Metamorphosis",
    body: [
      "Tale of Humankind Foundation and IIT Delhi's Office of Academic Outreach & New Initiatives ran Metamorphosis, a week of problem-finding and product-building for high-school students.",
      "GRID was the outreach partner and helped bring students into the programme. 150 students from 18 states took part and built 36 products.",
    ],
    media: {
      type: "photo",
      src: "/images/grid-photo-07-p7.jpg",
      alt: "Metamorphosis participants in matching shirts outside the Lecture Hall Complex at IIT Delhi",
      caption: "Metamorphosis participants outside the Lecture Hall Complex, IIT Delhi.",
      shape: "wide",
    },
    links: [["How we supported it", "/programmes/metamorphosis"]],
  },
  {
    id: "ch-5",
    date: "Jul 2026",
    title: "Conversations in Delhi",
    body: [
      "The team met Ajai Chowdhry, co-founder of HCL, visited AICTE and the Ministry of Education's Innovation Cell, and spoke with the Director of Skill Education at CBSE and the chairman of Sir Ganga Ram Hospital.",
      "These were meetings. A meeting is not an endorsement or a partnership.",
    ],
    media: {
      type: "photo",
      src: "/images/people-met/DamvNL9yFYV.jpg",
      alt: "Two GRID team members standing with Ajai Chowdhry in an office",
      caption: "With Ajai Chowdhry, co-founder of HCL, 10 July 2026.",
      shape: "square",
    },
    links: [["People we've met", "/meetings"]],
  },
  {
    id: "ch-6",
    date: "Aug 2026",
    title: "AAROH at IIIT Delhi",
    body: [
      "GRID organised AAROH with Tachyon India and CiPD, IIIT Delhi. Over two days, 1–2 August 2026, 363 young innovators, some as young as 15, built projects across AI/ML, game development, cybersecurity and hardware.",
      "School students did most of the organising. Thirteen teams were recognised. Maj Gen (Retd.) P. K. Sehgal felicitated the top teams, and Prof. Anuj Grover of IIIT Delhi supported the event.",
    ],
    media: {
      type: "photo",
      src: AAROH_IMAGES.roomWide,
      alt: "Students working at laptops across the AAROH hall at IIIT Delhi",
      caption: "The AAROH hall on day one.",
      shape: "wide",
    },
    links: [["Read the AAROH recap", "/programmes/aaroh"]],
  },
  {
    id: "ch-7",
    date: "Now",
    title: "What comes next",
    body: [
      "The next step is a clearer public home for GRID: programmes, problems worth solving, open opportunities and student work, all in one place.",
      "New events go on the upcoming page once dates, eligibility and fees are confirmed. Until then, you can join the team that runs it.",
    ],
    media: { type: "cta" },
  },
];

function SparkCard() {
  return (
    <div className="ab-spark">
      <span className="ab-kicker">Young Changemakers Bootcamp</span>
      <strong>Season 6</strong>
      <dl>
        <div><dt>Where</dt><dd>IIT Delhi campus</dd></div>
        <div><dt>Organised by</dt><dd>Tale of Humankind Foundation</dd></div>
      </dl>
      <img src="/images/partners/tale-of-humankind.webp" alt="Tale of Humankind Foundation logo" width="1600" height="1600" loading="lazy" />
    </div>
  );
}

function ChapterMedia({ media }) {
  if (media.type === "card") return <SparkCard />;
  if (media.type === "cta") {
    return (
      <div className="gh-actions ab-ch__cta">
        <Btn to="/programmes/upcoming">Upcoming events</Btn>
        <Btn to="/student-opportunities/team" variant="ghost">Join the team</Btn>
      </div>
    );
  }
  if (media.type === "portrait") {
    return (
      <div className="ab-ch__portrait">
        <ClipPhoto src={media.src} alt={media.alt} caption={media.caption} sizes="(max-width: 900px) 70vw, 22vw" />
        <dl>
          {media.stats.map(([value, label]) => (
            <div key={label}><dt>{value}</dt><dd>{label}</dd></div>
          ))}
        </dl>
      </div>
    );
  }
  return (
    <ClipPhoto
      className={`ab-ch__photo is-${media.shape}`}
      src={media.src}
      alt={media.alt}
      caption={media.caption}
      sizes="(max-width: 900px) 100vw, 50vw"
    />
  );
}

function Chapter({ chapter, index, reduceMotion }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.52", "start 0.44"] });
  const dotScale = useTransform(scrollYProgress, (value) => clamp01(value));
  const dateOpacity = useTransform(scrollYProgress, (value) => 0.28 + 0.72 * clamp01(value));
  return (
    <li className={`ab-ch ab-ch--${chapter.media.type}`} id={chapter.id} ref={ref}>
      <div className="ab-ch__date">
        <motion.span style={reduceMotion ? undefined : { opacity: dateOpacity }}>{chapter.date}</motion.span>
        <small>Chapter {index + 1}</small>
      </div>
      <span className="ab-ch__dot" aria-hidden="true">
        <motion.span style={reduceMotion ? undefined : { scale: dotScale }} />
      </span>
      <div className="ab-ch__body">
        <span className="ab-ch__mdate">{chapter.date}</span>
        <MaskWords as="h2" className="ab-ch__title" text={chapter.title} />
        {chapter.body.map((paragraph) => (
          <p key={paragraph.slice(0, 24)}>{paragraph}</p>
        ))}
        {chapter.links && (
          <div className="ab-ch__links">
            {chapter.links.map(([label, to]) => <ArrowLink key={to} to={to}>{label}</ArrowLink>)}
          </div>
        )}
        <div className="ab-ch__media">
          <ChapterMedia media={chapter.media} />
        </div>
      </div>
    </li>
  );
}

function Timeline() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.5", "end 0.5"] });
  const fill = useTransform(scrollYProgress, (value) => clamp01(value));
  const headTop = useTransform(scrollYProgress, (value) => `${clamp01(value) * 100}%`);
  return (
    <section className="ab-tl" aria-label="Timeline">
      <ol className="ab-tl__list" ref={ref}>
        <span className="ab-tl__rail" aria-hidden="true">
          <motion.span className="ab-tl__fill" style={reduceMotion ? { scaleY: 1 } : { scaleY: fill }} />
          {!reduceMotion && <motion.span className="ab-tl__head" style={{ top: headTop }} />}
        </span>
        {CHAPTERS.map((chapter, index) => (
          <Chapter key={chapter.id} chapter={chapter} index={index} reduceMotion={reduceMotion} />
        ))}
      </ol>
    </section>
  );
}

export default function JourneyPage() {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <PageFrame className="rd-page ab-page ab-page--journey">
      <Seo
        title="Our Journey"
        path="/journey"
        description="Follow the early journey of Project GRID from an idea to a youth-led innovation ecosystem."
      />
      <main>
        <section className="ab-jhero" aria-labelledby="ab-jhero-title">
          <div className="ab-jhero__copy">
            <motion.div {...appear(0)}><Kicker>Our story</Kicker></motion.div>
            <MaskWords as="h1" id="ab-jhero-title" className="ab-display ab-display--tight" text="How GRID began." onMount delay={0.08} />
            <motion.p className="ab-lede" {...appear(0.35)}>
              It started with two 16-year-olds at a bootcamp in December 2025. This page follows what happened next, one chapter at a time.
            </motion.p>
            <motion.div className="gh-actions" {...appear(0.45)}>
              <Btn href="#ch-1">Start reading</Btn>
              <Btn to="/team" variant="ghost">Meet the team</Btn>
            </motion.div>
          </div>
          <motion.nav className="ab-jhero__index" aria-label="Chapters" {...appear(0.55)}>
            <ol>
              {CHAPTERS.map((chapter) => (
                <li key={chapter.id}>
                  <a href={`#${chapter.id}`}>
                    <span>{chapter.date}</span>
                    <strong>{chapter.title}</strong>
                  </a>
                </li>
              ))}
            </ol>
          </motion.nav>
        </section>
        <Timeline />
      </main>
    </PageFrame>
  );
}

